import React, {useState, useCallback, useEffect, useRef} from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client';
import './ContactForm.css';
import { Form, Field } from 'react-final-form'
import { isValidEmail } from "../validations"

const SEND_CONTACT_MESSAGE = gql`mutation createContactFormSubmission($name: String!, $subject: String!, $email: String!, $message: String!, $website: String, $form_token: String){
  createContactFormSubmission(data: { name: $name, subject: $subject, email: $email, message: $message, website: $website, form_token: $form_token }){
    id:documentId
  }
}
`;

// A signed timestamp the backend hands out per rendered form. Sending it back
// with the message is what separates this form from a script POSTing the
// mutation at /graphql, which is where the spam has been coming from.
const TOKEN_URL = `${process.env.REACT_APP_API_ENDPOINT}/api/contact-form-token`

// The backend stops honouring a token after six hours. Refresh well short of
// that so a modal left open for an afternoon still sends on the first try.
const TOKEN_REFRESH_MS = 3 * 60 * 60 * 1000

const ContactForm = () => {
  const [messaged, setMessaged] = useState(false)
  const [onSubmission, setOnSubmission] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")
  const [messagingError, setMessagingError] = useState(null)
  const [SendContactMessage] = useMutation(SEND_CONTACT_MESSAGE);
  const formToken = useRef(null)

  const fetchToken = useCallback(async () => {
    try {
      const response = await fetch(TOKEN_URL)
      if (!response.ok) return null

      const { token } = await response.json()
      if (!token) return null

      formToken.current = { value: token, fetchedAt: Date.now() }
      return token
    } catch (error) {
      // Leave formToken alone and try again at submit time — a token we cannot
      // fetch should cost the message a spam flag, not the chance to send.
      return null
    }
  }, [])

  // Fetched on mount rather than on submit so its age also stands in for how
  // long the form was open; anything sent within seconds of rendering is not a
  // person typing.
  useEffect(() => { fetchToken() }, [fetchToken])

  const currentToken = async () => {
    const held = formToken.current
    if (held && Date.now() - held.fetchedAt < TOKEN_REFRESH_MS) return held.value
    return (await fetchToken()) || (held && held.value) || ""
  }

  const SubmitContactForm = async (payload) =>{
    setOnSubmission(true)
    setMessagingError("")
    const form_token = await currentToken()
    SendContactMessage({ variables: { name: payload.contactForm.name, subject: payload.contactForm.subject || "General Inquiry", email: payload.contactForm.email, message: payload.contactForm.message, website: payload.contactForm.website || "", form_token } }).then(function (value) {
      setOnSubmission(false)
      setMessaged(true)
      setNotificationMessage("Message successfully sent, thank you.")
    }, function (err) {
      setOnSubmission(false)
      setMessaged(false)
      setMessagingError("An error occured sending your message, check your internet connection and try again.")
    });
  }

  return (
    <Form
      onSubmit={SubmitContactForm}
      validate={(values) => {
        let errors = {contactForm:{}}

        if(!values.contactForm || isValidEmail(values.contactForm.email)){
          errors.contactForm.email = "Invalid Email" 
        }

        if (!values.contactForm || !values.contactForm.name) {
          errors.contactForm.name = "This field is required"
        }

        if (!values.contactForm || !values.contactForm.message) {
          errors.contactForm.message = "This field is required"
        }
        return errors
      }}

      render={({ handleSubmit, submitting, valid, dirty, pristine, touched, submitSucceeded }) => (
        <div>
        <form onSubmit={handleSubmit} id="contact_form" className={`${messaged ? "hidden" : ""}`}>
          <fieldset>
            {/* Honeypot. Positioned off-screen rather than hidden outright,
                because a bot that can tell a field is not rendered will skip
                it — the point is that it looks fillable. Nobody can see it, so
                the backend refuses any submission that arrives with it set. */}
            <Field name={"contactForm[website]"}>
              {({ input }) => (
                <input
                  {...input}
                  type="text"
                  className="honeypot"
                  placeholder="Website"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                />
              )}
            </Field>
            <legend>Contact form</legend>
            <div className="row large-12 column">
            </div>

            <div className="row inputrow">
              <div className="large-12 columns">
                <Field name={"contactForm[name]"}>
                {({ input, meta }) => (
                  <div>
                    <input {...input} type="text" maxLength="50" placeholder="* Your Name" className={`${meta.error &&meta.touched ? "error" : ""}`}/>
                    {meta.error && meta.touched && <small className="errorMessage">{meta.error}</small>}
                  </div>
                )}
              </Field>
              </div>
            </div>

            <div className="row inputrow">
              <div className="large-12 columns">
                <Field type="text" name={"contactForm[subject]"} maxLength="50" component="input" placeholder="Subject" />
              </div>
            </div>

            <div className="row inputrow">
              <div className="large-12 columns">
                <Field name={"contactForm[email]"}>
                {({ input, meta }) => (
                  <div>
                    <input {...input} type="email" maxLength="254" placeholder="* Your Email" className={`${meta.error &&meta.touched ? "error" : ""}`}/>
                    {meta.error && meta.touched && <small className="errorMessage">{meta.error}</small>}
                  </div>
                )}
                </Field>
              </div>
            </div>

            <div className="row inputrow">
              <div className="large-12 columns">
                <Field name={"contactForm[message]"} >
                {({ input, meta }) => (
                  <div>
                    <textarea {...input} type="text" maxLength="2000" placeholder="* YOUR MESSAGE" className={`${meta.error &&meta.touched ? "error" : ""}`}/>
                    {meta.error && meta.touched && <small className="errorMessage">{meta.error}</small>}
                  </div>
                )}
                </Field>
              </div>
            </div>

            <div className="row inputrow" style={{backgroundColor:"black"}}>
              <div className="column large-12">
              { onSubmission ?
                <span style={{margin: "1rem 0 1rem 1rem", fontWeight:"bold", textAlign:"center"}}><small>Sending...</small></span>
                : null
              }
              { messagingError ?
                <span style={{marginLeft: "1rem", justifyContent: "center"}}><small className="errorMessage">{messagingError}</small></span>
                : null
              }
                <button name="contact_submit" className="button right radius" style={{padding:"0.2em 0.5em"}} disabled={submitting || !valid || messaged} type="submit">SEND</button>
              </div>
            </div>

          </fieldset>
        </form>
        { messaged?
          <div style={{margin: "1rem 0 1rem 1rem", fontWeight:"bold", textAlign:"center"}}><small style={{color: "#69EBF1"}}>{notificationMessage}</small></div>
          : null
        }

        
        </div>
      )}
    />
  );
}

export default ContactForm;
